import { InterfacePreferences } from "@outschool/gql-backend-generated";
// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import { isLocalStorageSupported } from "@outschool/local-storage";
import * as Sentry from "@outschool/sentry/dist/client";
import { useSession } from "@outschool/ui-auth";
import gql from "graphql-tag";
import * as _ from "lodash";
import React from "react";
import { useMutation } from "react-apollo";

import {
  ACTIVITY_FORMAT_DESCRIPTION_FLEX_HIDDEN,
  ACTIVITY_FORMAT_DESCRIPTION_MULTI_DAY_HIDDEN,
  ACTIVITY_FORMAT_DESCRIPTION_ONE_TIME_HIDDEN,
  ACTIVITY_FORMAT_DESCRIPTION_ONGOING_HIDDEN
} from "../../shared/Activity";
import {
  cleanTypeNameField,
  validOptimisticResponse
} from "../../shared/client/ApolloClient";
import {
  CurrentUserQueryQuery,
  CurrentUserQueryQueryVariables,
  UpdateCurrentUserInterfacePreferencesMutation,
  UpdateCurrentUserInterfacePreferencesMutationVariables
} from "../../shared/generated/graphql";
import { currentUserQuery } from "../queries/CurrentUserQuery";
import { useAppState } from "../stores/AppStateProvider";

export const DEFAULT_SCHEDULE_FILTERS = "defaultScheduleFilters" as const;
export const USER_INTERFACE_PREFERENCES = "userInterfacePreferences";
const localStorage = isLocalStorageSupported();

const DEFAULTS: InterfacePreferences = {
  [ACTIVITY_FORMAT_DESCRIPTION_FLEX_HIDDEN]: false,
  [ACTIVITY_FORMAT_DESCRIPTION_ONGOING_HIDDEN]: false,
  [ACTIVITY_FORMAT_DESCRIPTION_ONE_TIME_HIDDEN]: false,
  [ACTIVITY_FORMAT_DESCRIPTION_MULTI_DAY_HIDDEN]: false,
  [DEFAULT_SCHEDULE_FILTERS]: {
    dow: null,
    endBy: null,
    endByTime: null,
    startAfter: null,
    startAfterTime: null
  }
};

function isDefaultScheduleFilters(
  value: InterfacePreferences[keyof InterfacePreferences]
): value is InterfacePreferences["defaultScheduleFilters"] {
  return typeof value === "object";
}

const UpdateCurrentUserInterfacePreferencesMutationStr = gql`
  mutation UpdateCurrentUserInterfacePreferences(
    $prefs: InterfacePreferencesInput
  ) {
    updateCurrentUserInterfacePreferences(interfacePreferences: $prefs)
  }
`;

type Context = {
  userInterfacePreferences: InterfacePreferences;
  updateUserInterfacePreferencesFromUser(currentUser?: {
    interfacePreferences?: InterfacePreferences;
  }): void;
  updateUserInterfacePreferences<K extends keyof InterfacePreferences>(
    key: K,
    value: InterfacePreferences[K]
  ): Promise<void>;
  getPreference<K extends keyof InterfacePreferences>(
    key: K
  ): InterfacePreferences[K] | null;
  setPreference: (
    key: keyof InterfacePreferences,
    valueOrFn: any
  ) => Promise<void>;
};

const UserInterfacePreferencesContext = React.createContext<Context>({
  userInterfacePreferences: DEFAULTS,
  updateUserInterfacePreferencesFromUser: (_cu?: {
    interfacePreferences?: InterfacePreferences;
  }) => null,
  updateUserInterfacePreferences: <K extends keyof InterfacePreferences>(
    _k: K,
    _v: InterfacePreferences[K]
  ) => Promise.resolve(),
  getPreference: <K extends keyof InterfacePreferences>(_key: K) => null,
  setPreference: (_key: keyof InterfacePreferences, _valueOrFn: any) =>
    Promise.resolve()
});

export const UserInterfacePreferencesProvider: React.FC = ({ children }) => {
  const appState = useAppState();
  const { isLoggedIn, currentUser } = useSession();

  const [updateUserInterfacePreferencesMutation] = useMutation<
    UpdateCurrentUserInterfacePreferencesMutation,
    UpdateCurrentUserInterfacePreferencesMutationVariables
  >(UpdateCurrentUserInterfacePreferencesMutationStr);

  let initialUserInterfacePreferences = _.cloneDeep(DEFAULTS);
  if (localStorage) {
    try {
      const userInterfacePreferencesFromLocalStorage = localStorage.getItem(
        USER_INTERFACE_PREFERENCES
      );
      initialUserInterfacePreferences =
        userInterfacePreferencesFromLocalStorage !== null
          ? JSON.parse(userInterfacePreferencesFromLocalStorage)
          : _.cloneDeep(DEFAULTS);
    } catch (e) {
      if (e instanceof SyntaxError) {
        initialUserInterfacePreferences = _.cloneDeep(DEFAULTS);
      } else {
        Sentry.logException(e);
      }
    }
  }
  const [userInterfacePreferences, setUserInterfacePreferences] =
    React.useState<InterfacePreferences>(initialUserInterfacePreferences);

  const updateUserInterfacePreferencesFromUser = React.useCallback(
    (currentUser?: { interfacePreferences?: InterfacePreferences }) => {
      if (!currentUser?.interfacePreferences) {
        return;
      }
      const updatedUserInterfacePreferences = cleanTypeNameField(
        currentUser.interfacePreferences
      );
      if (localStorage) {
        localStorage.setItem(
          USER_INTERFACE_PREFERENCES,
          JSON.stringify(updatedUserInterfacePreferences)
        );
      }
      setUserInterfacePreferences(updatedUserInterfacePreferences);
    },
    [setUserInterfacePreferences]
  );

  React.useEffect(() => {
    appState.setUpdateUserInterfacePreferencesFromUser(
      updateUserInterfacePreferencesFromUser
    );
  }, [appState, updateUserInterfacePreferencesFromUser]);

  const updateUserInterfacePreferences = React.useCallback(
    async <K extends keyof InterfacePreferences>(
      key: K,
      value: InterfacePreferences[K]
    ) => {
      const updatedUserInterfacePreferences = {
        ...userInterfacePreferences,
        [key]: value
      };
      setUserInterfacePreferences(updatedUserInterfacePreferences);
      if (localStorage) {
        localStorage.setItem(
          USER_INTERFACE_PREFERENCES,
          JSON.stringify(updatedUserInterfacePreferences)
        );
      }
      if (isLoggedIn) {
        await updateUserInterfacePreferencesMutation({
          variables: {
            prefs: {
              [key]: isDefaultScheduleFilters(value)
                ? _.omit<InterfacePreferences["defaultScheduleFilters"]>(
                    value,
                    "__typename"
                  )
                : value
            }
          },
          update: store => {
            const data = store.readQuery<
              CurrentUserQueryQuery,
              CurrentUserQueryQueryVariables
            >({ query: currentUserQuery });
            const interfacePreferences = data?.currentUser?.interfacePreferences
              ? { ...data.currentUser.interfacePreferences, [key]: value }
              : { [key]: value };
            store.writeQuery({
              query: currentUserQuery,
              data: {
                ...data,
                currentUser: { ...data?.currentUser, interfacePreferences }
              }
            });
          },
          optimisticResponse: validOptimisticResponse({
            __typename: "Mutation",
            updateCurrentUserInterfacePreferences: true
          })
        });
      }
    },
    [
      isLoggedIn,
      updateUserInterfacePreferencesMutation,
      userInterfacePreferences
    ]
  );

  const getPreference = React.useCallback(
    <K extends keyof InterfacePreferences>(key: K): InterfacePreferences[K] => {
      if (currentUser?.interfacePreferences) {
        return currentUser.interfacePreferences[key];
      }

      const value = userInterfacePreferences[key];
      if (value === undefined) {
        return _.cloneDeep(DEFAULTS)[key];
      }
      return value;
    },
    [currentUser?.interfacePreferences, userInterfacePreferences]
  );

  const setPreference = React.useCallback(
    async (key: keyof InterfacePreferences, valueOrFn) => {
      let value;
      if (typeof valueOrFn === "function") {
        const oldValue = getPreference(key);
        value = valueOrFn(oldValue);
      } else {
        value = valueOrFn;
      }
      await updateUserInterfacePreferences(key, value);
    },
    [getPreference, updateUserInterfacePreferences]
  );

  return (
    <UserInterfacePreferencesContext.Provider
      value={{
        userInterfacePreferences,
        updateUserInterfacePreferencesFromUser,
        updateUserInterfacePreferences,
        getPreference,
        setPreference
      }}
    >
      {children}
    </UserInterfacePreferencesContext.Provider>
  );
};

export const useUserInterfacePreferencesContext = () => {
  return React.useContext(UserInterfacePreferencesContext);
};
